import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { checkoutError, validateCheckout } from "@/lib/checkoutValidation";
import { TERMS_VERSION } from "@/lib/legalContent";

const EMPTY = { name: "", phone: "", address: "", note: "" };
export const useCheckout = () => {
  const cart = useCart();
  const { user, refresh } = useAuth();
  const [customer, setCustomer] = useState(EMPTY), [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const placingRef = useRef(false), previousOwner = useRef(user?.user_id);
  const navigate = useNavigate();
  const cartIds = useMemo(() => cart.items.map((i) => i.product.id), [cart.items]);
  useEffect(() => {
    setTermsAccepted(false);
    if (previousOwner.current !== user?.user_id) setCustomer(EMPTY);
    previousOwner.current = user?.user_id;
    if (user) setCustomer((c) => ({ ...c, name: c.name || user.name, phone: c.phone || user.phone || "", address: c.address || user.address || "" }));
  }, [user]);
  const set = (key) => (e) => { setError(""); setCustomer((c) => ({ ...c, [key]: e.target.value })); };
  const placeOrder = async (event) => {
    event?.preventDefault();
    if (placingRef.current) return;
    const validation = validateCheckout(customer, cart.items);
    if (validation) { setError(validation); toast.error(validation); return; }
    if (!termsAccepted) { setError("Please review and accept the Terms and made-to-order policy before placing your order."); return; }
    setError("");
    placingRef.current = true; setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/orders`, { ...customer, name: customer.name.trim(), phone: customer.phone.trim(), address: customer.address.trim(), note: customer.note.trim(), terms_accepted: true, terms_version: TERMS_VERSION, items: cart.items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })) }, { timeout: 30000 });
      if (!data?.order_id) throw new Error("Order acknowledgement missing");
      cart.clear(); cart.setOpen(false);
      setTermsAccepted(false);
      if (user) refresh();
      navigate(`/order/${data.order_id}`);
    } catch (err) { const message = checkoutError(err); setError(message); toast.error(message); }
    finally { placingRef.current = false; setPlacing(false); }
  };
  return { ...cart, user, customer, placing, cartIds, error, set, placeOrder, termsAccepted, setTermsAccepted };
};